// @givmo/app-admin — Settings → Security → the COMPANY SIGN-ON section (AS-WEB6).
//
// Where a company Owner takes themselves from nothing to company single sign-on without Givmo staff:
// verify the domain → connect the identity provider → test it → choose the tier. The order is the
// backend's, enforced there and merely rendered here, so a step that is not yet reachable says why
// rather than offering a control whose write is guaranteed to be refused.
//
// ══ THE HONEST-CLAIMS RAIL, AS IT APPLIES HERE ════════════════════════════════════════════════
// Same rail as the MFA section above it, with two shapes specific to this surface:
//
//   1. THE SETUP GUIDANCE IS THE SERVER'S, RENDERED VERBATIM. `instructions`, `setup_guidance` and
//      the test-connection `guidance` are composed backend-side because they name THIS company's
//      record and THIS company's verified domain. This file never paraphrases them and never
//      substitutes copy of its own for them — a second copy of a security instruction is a second
//      thing to keep in step, and the server's is the one that knows the facts.
//   2. A CLAMPED SAVE IS REPORTED, NOT SWALLOWED. `PUT …/policy` answers 200 with the WHOLE config,
//      and the tier in it may not be the tier that was sent (the backend clamps a tier above `none`
//      to `none` when a precondition is missing). So the save path compares what came back against
//      what it asked for and says so. Treating the 200 as confirmation would tell an Owner their
//      company is locked down when it is not.
//
// ══ DARK — THE SERVER ANSWERS, THERE IS NO CLIENT FLAG ════════════════════════════════════════
// The section renders when `GET /admin/company/settings/sso` answers 200, and not otherwise. That
// route sits behind the same `EMPLOYER_SSO_ENABLED` router dependency as every write here, so asking
// IS the gate: 404 (dark), 403, 500 and a transport failure all hide it, and there is no `VITE_*`
// switch that could fall out of step with the backend flag. The probe lives in
// `settings-security-tab.tsx` beside the MFA one, because the TAB's visibility depends on both.
//
// A 404 on a WRITE is still handled as skew rather than failure: the surface can go dark between the
// probe and the click.
//
// The one thing that read does NOT carry is the pair of values an IT admin pastes into their
// identity provider: the broker assigns them, and the probe asks the broker nothing. They have their
// own read (`GET …/sso/connection`, AS-WEB25), which a save or "Get Givmo's values" seeds (AS-WEB28:
// the values can exist before the provider's details) and which fails on its own without taking the
// section with it — see `RelyingPartyValues`.
//
// ══ THE TEST CONNECTION IS A REAL SIGN-IN ═════════════════════════════════════════════════════
// It starts the same ceremony an employee takes, in a new window, and produces nothing — no
// credential, no linked identity, no assertion stamp. The outcome is read from the Owner-gated
// endpoint by POLLING, not from the URL the browser comes back on: the backend deliberately puts no
// verdict and no id in that redirect, because a company's identity-provider diagnostic does not
// belong in browser history or in a `Referer`. The ceremony window is opened and then DISOWNED
// (`opener = null`), so there is no channel back from it either — the poll is the whole mechanism.
// `sso-test-session.ts` owns the round trip and explains why the opener handle is not on offer.

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { isGivmoError } from '@givmo/api-client';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Field,
  RadioTile,
  RadioTileGroup,
  Select,
  Skeleton,
  Textarea,
  testIdProp,
  toast,
} from '@givmo/design-system';
import { SsoErrorNote } from './sso-error-note';
import { ScimPanel } from './sso-scim-panel';
import { SessionResetCard } from './session-reset-card';
import { copyToClipboard } from './copy-to-clipboard';
import { SSO_COPY, SSO_COPY_DRAFT, domainInstructionBlock } from './security-copy';
import {
  canSubmitTier,
  domainStep,
  formatVerifiedAt,
  freshnessOptions,
  idpStatusLine,
  isTierWritable,
  readSsoConfig as decodeSsoConfig,
  readSsoConnection as decodeSsoConnection,
  readTestConnection as decodeTestConnection,
  selectableTiers,
  testIsPending,
  testPresentation,
  tierWasClamped,
  type SsoConfigState,
  type SsoConnectionState,
  type SsoTier,
} from './sso-decode';
import {
  readSsoConfig,
  readSsoConnection,
  readTestConnection,
  saveSsoConnection,
  saveSsoPolicy,
  startDomainVerification,
  checkDomainVerification,
  startSsoConnection,
  startTestConnection,
  type SsoConnectionBody,
} from './sso-config-transport';
import { createPkcePair } from './sso-pkce';
import {
  forgetSsoTest,
  recallSsoTest,
  rememberSsoTest,
  ssoTestRedirectUri,
} from './sso-test-session';
import { SsoLockCeremonyDialog } from './sso-lock-ceremony-dialog';
import { useSecurityHost } from './security-host';
import { WRAPPING_CARD_TITLE } from './card-title';

const PREFIX = 'settings-security-sso';

/** Each tier's words: L0 and L3 are approved, L4's are the sign-in lock's draft (`security-copy.ts`). */
const TIER_COPY = [...SSO_COPY.tiers, ...SSO_COPY_DRAFT.tiers];

/** The section's config read. Distinct from the tab's one-shot probe: this one refetches. */
export const SSO_CONFIG_QUERY_KEY = ['admin-company-sso-config'] as const;

/**
 * The relying-party values' own read (AS-WEB25), `GET …/sso/connection`. Deliberately NOT under
 * {@link SSO_CONFIG_QUERY_KEY}: invalidating the config matches by key prefix, and the values must
 * not be re-read — let alone wiped — by every write that invalidates the config.
 */
export const SSO_CONNECTION_QUERY_KEY = ['admin-company-sso-connection'] as const;

/** The test-connection poll interval while a ceremony is still `pending`. */
export const TEST_POLL_INTERVAL_MS = 3_000;

export interface SsoSectionProps {
  /** The company, from the settings page's single `GET /admin/company`. Its name drives the L4
   *  ceremony's typed confirmation. */
  companyName: string;
  /** The body the tab's dark-gate probe served — the full config, so the first paint needs no
   *  second request. Production always has one (the section renders only after a 200). */
  probedConfig?: unknown;
}

export function SsoSection({ companyName, probedConfig }: SsoSectionProps) {
  const queryClient = useQueryClient();

  const configQuery = useQuery({
    queryKey: SSO_CONFIG_QUERY_KEY,
    queryFn: ({ signal }) => readSsoConfig(signal),
    select: decodeSsoConfig,
    ...(probedConfig === undefined ? {} : { initialData: probedConfig }),
  });

  if (configQuery.isPending) {
    return (
      <Card>
        <CardHeader className={WRAPPING_CARD_TITLE} title={SSO_COPY.sectionTitle} />
        <CardBody>
          <div className="flex flex-col gap-12" {...testIdProp(`${PREFIX}-loading`)}>
            <Skeleton className="h-12 w-1/3" />
            <Skeleton className="h-12 w-2/3" />
          </div>
        </CardBody>
      </Card>
    );
  }

  if (configQuery.isError) {
    return (
      <Card>
        <CardHeader className={WRAPPING_CARD_TITLE} title={SSO_COPY.sectionTitle} />
        <CardBody>
          <SsoErrorNote
            error={configQuery.error}
            fallback={SSO_COPY.unavailable}
            testId={`${PREFIX}-config-error`}
          />
        </CardBody>
      </Card>
    );
  }

  return (
    <SsoSectionBody
      config={configQuery.data}
      companyName={companyName}
      invalidate={() => void queryClient.invalidateQueries({ queryKey: SSO_CONFIG_QUERY_KEY })}
      writeConfig={(next) => queryClient.setQueryData(SSO_CONFIG_QUERY_KEY, next)}
      mergeConfig={(key, block) =>
        queryClient.setQueryData(SSO_CONFIG_QUERY_KEY, (old: unknown) => ({
          ...(typeof old === 'object' && old !== null ? old : {}),
          [key]: block,
        }))
      }
    />
  );
}

interface SsoSectionBodyProps {
  config: SsoConfigState;
  companyName: string;
  invalidate: () => void;
  writeConfig: (next: unknown) => void;
  mergeConfig: (key: 'domain_verification' | 'connection' | 'scim', block: unknown) => void;
}

function SsoSectionBody({
  config,
  companyName,
  invalidate,
  writeConfig,
  mergeConfig,
}: SsoSectionBodyProps) {
  return (
    <div className="flex flex-col gap-16" {...testIdProp(`${PREFIX}-section`)}>
      <div>
        <h3 className="font-sans text-s16 font-w700 tracking-givmo text-text-strong">
          {SSO_COPY.sectionTitle}
        </h3>
        <p className="pt-4 font-sans text-s14 font-w400 leading-h15 tracking-normal text-text-secondary">
          {SSO_COPY.sectionIntro}
        </p>
      </div>

      <DomainStep config={config} invalidate={invalidate} mergeConfig={mergeConfig} />
      <ConnectionStep config={config} invalidate={invalidate} mergeConfig={mergeConfig} />
      <TestConnectionStep config={config} />
      <TierStep config={config} companyName={companyName} writeConfig={writeConfig} />
      {/* AS-WEB7 — directory sync. LAST rather than a numbered step, because it is not one: it needs
          the same two preconditions Steps 1 and 2 establish, but it is independent of the tier and an
          employer can run either without the other. It merges its own block and does NOT invalidate
          — see `sso-scim-panel.tsx`, which explains why a refetch here would take the endpoint URL
          off the screen mid-setup. */}
      <ScimPanel
        config={config}
        mergeScim={(block) => mergeConfig('scim', block)}
        invalidateConfig={invalidate}
      />
      {/* AS-WEB7 / AS-BE19 — the workforce session reset. It lives HERE rather than beside the audit
          export because it rides `EMPLOYER_SSO_ENABLED` alone, exactly as this section does, so its
          visibility is already decided by the section's own probe. The stored tier is threaded
          through for one sentence: only under a stored sign-in lock does signing everyone out send
          them back through an identity provider (AS-WEB22), and anywhere else that claim would be
          false. */}
      <SessionResetCard mode={config.mode} />
    </div>
  );
}

// ── Step 1 · domain verification ────────────────────────────────────────────────────────────────

function DomainStep({
  config,
  invalidate,
  mergeConfig,
}: {
  config: SsoConfigState;
  invalidate: () => void;
  mergeConfig: SsoSectionBodyProps['mergeConfig'];
}) {
  const domain = config.domain;
  const step = domainStep(domain);
  const [confirmingRemint, setConfirmingRemint] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);

  // ⚠ EVERY WRITE ON THIS SURFACE INVALIDATES THE WHOLE CONFIG, not just the block it returned.
  // `can_enable_sso` is DERIVED server-side from the verification stamp and the connection, so a
  // re-mint (which clears the stamp) or a successful check flips a field that is not in the response
  // being merged. Merging alone would leave the tier step offering — or refusing — options on a
  // stale precondition. The merge is for the instant paint; the invalidate is for correctness.
  const settle = (block: unknown) => {
    mergeConfig('domain_verification', block);
    invalidate();
  };

  const start = useMutation({
    mutationFn: startDomainVerification,
    onSuccess: (result) => {
      setConfirmingRemint(false);
      settle(result);
    },
  });

  const check = useMutation({
    mutationFn: checkDomainVerification,
    onSuccess: settle,
  });

  const verifiedAt = formatVerifiedAt(domain.verifiedAt);

  return (
    <Card>
      <CardHeader
        className={WRAPPING_CARD_TITLE}
        title={SSO_COPY.domainTitle}
        subtitle={SSO_COPY.domainSubtitle}
      />
      <CardBody>
        <div className="flex flex-col gap-12" {...testIdProp(`${PREFIX}-domain`)}>
          {step === 'unusable' && (
            <p
              className="font-sans text-s14 font-w400 leading-h15 tracking-normal text-text-secondary"
              {...testIdProp(`${PREFIX}-domain-unusable`)}
            >
              {SSO_COPY.domainUnusable}
            </p>
          )}

          {step === 'verified' && (
            <div className="flex flex-col gap-8" {...testIdProp(`${PREFIX}-domain-verified`)}>
              <p className="font-sans text-s14 font-w600 tracking-givmo text-success-700">
                {verifiedAt === null
                  ? `${SSO_COPY.domainVerifiedTitle} — ${domain.domain ?? ''}`.trim()
                  : `${SSO_COPY.domainVerifiedTitle} — ${domain.domain ?? ''} · ${verifiedAt}`}
              </p>
            </div>
          )}

          {step === 'idle' && (
            <p className="font-sans text-s14 font-w400 leading-h15 tracking-normal text-text-secondary">
              {domain.domain}
            </p>
          )}

          {step === 'awaiting-record' && (
            <DomainRecord
              recordName={domain.recordName ?? ''}
              token={domain.token ?? ''}
              instructions={domain.instructions}
              copyFailed={copyFailed}
              onCopyFailed={setCopyFailed}
            />
          )}

          {/* The check's failure IS the differentiated guidance. "We looked and it is not there" and
              "we could not look" share ONE error code (`DOMAIN_VERIFICATION_FAILED`) and differ only
              in the message the backend composed, which names this company's own record for the
              first case and explicitly declines to blame the employer's DNS for the second. So the
              server's sentence is rendered rather than re-derived — see `sso-config-transport.ts`. */}
          {check.isError && (
            <SsoErrorNote
              error={check.error}
              fallback={SSO_COPY.domainCheckFailed}
              testId={`${PREFIX}-domain-check-error`}
            />
          )}
          {start.isError && (
            <SsoErrorNote
              error={start.error}
              fallback={SSO_COPY.domainNotStarted}
              testId={`${PREFIX}-domain-start-error`}
            />
          )}

          {domain.checkMessage !== null && (
            <p
              className="font-sans text-s13 font-w400 leading-h15 tracking-normal text-text-secondary"
              {...testIdProp(`${PREFIX}-domain-check-message`)}
            >
              {domain.checkMessage}
            </p>
          )}

          {confirmingRemint ? (
            <div
              className="flex flex-col items-start gap-8 rounded-lg border border-warning-100 bg-warning-50 p-16"
              {...testIdProp(`${PREFIX}-domain-remint-warning`)}
            >
              <p className="font-sans text-s13 font-w400 leading-h15 text-warning-700">
                {SSO_COPY.domainRemintWarning}
              </p>
              <div className="flex items-center gap-12">
                <Button
                  appearance="saas"
                  tone="secondary"
                  onClick={() => setConfirmingRemint(false)}
                  testId={`${PREFIX}-domain-remint-cancel`}
                >
                  {SSO_COPY.domainRemintCancel}
                </Button>
                <Button
                  appearance="saas"
                  tone="primary"
                  loading={start.isPending}
                  onClick={() => start.mutate()}
                  testId={`${PREFIX}-domain-remint-confirm`}
                >
                  {SSO_COPY.domainRemintConfirm}
                </Button>
              </div>
            </div>
          ) : (
            step !== 'unusable' && (
              <div className="flex flex-wrap items-center gap-12">
                {step === 'idle' && (
                  <Button
                    appearance="saas"
                    tone="primary"
                    loading={start.isPending}
                    onClick={() => start.mutate()}
                    testId={`${PREFIX}-domain-start`}
                  >
                    {SSO_COPY.domainStart}
                  </Button>
                )}
                {step === 'awaiting-record' && (
                  <Button
                    appearance="saas"
                    tone="primary"
                    loading={check.isPending}
                    onClick={() => check.mutate()}
                    testId={`${PREFIX}-domain-check`}
                  >
                    {check.isPending ? SSO_COPY.domainChecking : SSO_COPY.domainCheck}
                  </Button>
                )}
                {/* Re-minting is destructive (it clears a live verification), so it is a
                    plain secondary action behind an explicit warning, never the primary one. */}
                {step !== 'idle' && (
                  <Button
                    appearance="saas"
                    tone="secondary"
                    onClick={() => setConfirmingRemint(true)}
                    testId={`${PREFIX}-domain-remint`}
                  >
                    {SSO_COPY.domainRemint}
                  </Button>
                )}
              </div>
            )
          )}
        </div>
      </CardBody>
    </Card>
  );
}

/**
 * The record to publish, plus the hand-off block.
 *
 * The hand-off block (Jonathan's 08-25 rider) is a SELF-CONTAINED work order an admin can paste to a
 * registrar's support desk, into their own AI assistant, or next to their DNS provider's API docs.
 * It is composed client-side and rendered ALONGSIDE — never instead of — the server's own
 * `instructions`, which stay the on-screen next step.
 */
function DomainRecord({
  recordName,
  token,
  instructions,
  copyFailed,
  onCopyFailed,
}: {
  recordName: string;
  token: string;
  instructions: string | null;
  copyFailed: boolean;
  onCopyFailed: (failed: boolean) => void;
}) {
  const block = domainInstructionBlock(recordName, token);

  const copy = async () => {
    // The block is on screen and selectable, so a refused clipboard is a missing convenience rather
    // than a dead end. Say so instead of pretending it copied — which is what the earlier
    // `await navigator.clipboard?.writeText(block)` did whenever the API was ABSENT rather than
    // refusing, since the optional call resolves to `undefined` and never reaches a `catch`. See
    // `copy-to-clipboard.ts`.
    const copied = await copyToClipboard(block);
    onCopyFailed(!copied);
    if (copied) toast.success(SSO_COPY.domainHandoffCopied);
  };

  return (
    <div className="flex flex-col gap-12">
      {/* The server's sentence carries the record's long token, so it may break INSIDE the token
          (`overflow-wrap: anywhere`, D-AS-38 (1)) rather than push the page past a phone's edge. */}
      {instructions !== null && (
        <p
          className="font-sans text-s14 font-w400 leading-h15 tracking-normal text-text-secondary wrap-anywhere"
          {...testIdProp(`${PREFIX}-domain-instructions`)}
        >
          {instructions}
        </p>
      )}

      <div className="flex flex-col gap-8 rounded-lg border border-border-subtle bg-surface-sunken p-16">
        <RecordRow
          label={SSO_COPY.domainRecordTypeLabel}
          value={SSO_COPY.domainRecordType}
          testId={`${PREFIX}-domain-record-type`}
        />
        <RecordRow
          label={SSO_COPY.domainRecordNameLabel}
          value={recordName}
          testId={`${PREFIX}-domain-record-name`}
        />
        <RecordRow
          label={SSO_COPY.domainRecordValueLabel}
          value={token}
          testId={`${PREFIX}-domain-record-value`}
        />
      </div>

      <div className="flex flex-col gap-8">
        <p className="font-sans text-s13 font-w600 tracking-givmo text-text-strong">
          {SSO_COPY.domainHandoffTitle}
        </p>
        <p className="font-sans text-s12 font-w400 leading-h15 tracking-normal text-text-tertiary">
          {SSO_COPY.domainHandoffHelp}
        </p>
        <pre
          className="overflow-x-auto whitespace-pre-wrap break-words rounded-lg border border-border-subtle bg-surface-sunken p-16 font-mono text-s12 text-text-secondary"
          {...testIdProp(`${PREFIX}-domain-handoff`)}
        >
          {block}
        </pre>
        <div className="flex items-center gap-12">
          <Button
            appearance="saas"
            tone="secondary"
            size="sm"
            onClick={() => void copy()}
            testId={`${PREFIX}-domain-handoff-copy`}
          >
            {SSO_COPY.domainHandoffCopy}
          </Button>
          {copyFailed && (
            <span
              className="font-sans text-s12 text-text-tertiary"
              {...testIdProp(`${PREFIX}-domain-handoff-copy-failed`)}
            >
              {SSO_COPY.domainHandoffCopyFailed}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function RecordRow({ label, value, testId }: { label: string; value: string; testId: string }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-8">
      <span className="font-sans text-s13 text-text-secondary">{label}</span>
      <span
        className="break-all font-mono text-s13 text-text-strong select-all"
        {...testIdProp(testId)}
      >
        {value}
      </span>
    </div>
  );
}

// ── Step 2 · the IdP connection ─────────────────────────────────────────────────────────────────
//
// TWO PARTS, GIVMO'S FIRST (AS-WEB28, D-AS-47). Okta's app wizard asks for Givmo's two values — the
// entity ID and the reply URL — before it shows the metadata URL the form below takes, and the
// broker can create the connection, and so the values, with no identity provider in it. So the
// values come first, one Owner click away (`POST …/connection/start`), and the provider's details
// complete that same connection later. A provider that gives its metadata first (Google) still
// starts at the form: saving creates the connection and its values appear above.
//
// ⚠ VALUES ON SCREEN NO LONGER MEAN "CONNECTED". They can exist before anything is saved, so the
// card says which it is: the success line once the section's read says `configured` AND the values'
// read has them; "Givmo connects … once you save these" while they wait for the details; and the
// lost-connection cue (AS-WEB29) when the section says configured and the service holds nothing.
//
// ⚠ THE TWO WRITES NEVER RUN TOGETHER FROM ONE TAB. Each reads the broker's connection and creates
// one when there is none, and the broker's create is additive, so "Get Givmo's values" pressed
// while a save is in flight (or Save while the values are on their way) made two connections — and
// sometimes a saved company whose primary had no identity provider in it, under the success line
// (cut 1007c's review). The backend now serializes them per company; each button here also waits
// for the other's request.

function hasRelyingPartyValues(state: SsoConnectionState | undefined): state is SsoConnectionState {
  return (
    state !== undefined && (state.relyingPartyId !== null || state.relyingPartyCallback !== null)
  );
}

function ConnectionStep({
  config,
  invalidate,
  mergeConfig,
}: {
  config: SsoConfigState;
  invalidate: () => void;
  mergeConfig: SsoSectionBodyProps['mergeConfig'];
}) {
  // Metadata URL is the default because it is the shape that keeps working when the provider
  // rotates its signing certificate. The manual trio is the escape hatch for a provider that does
  // not publish one reachable from our network.
  const [manual, setManual] = useState(false);
  const [metadataUrl, setMetadataUrl] = useState('');
  const [entityId, setEntityId] = useState('');
  const [signOnUrl, setSignOnUrl] = useState('');
  const [certificate, setCertificate] = useState('');
  const queryClient = useQueryClient();
  const verified = config.domain.verified;
  const configured = config.connection.configured;

  // The values' own read (AS-WEB25), asked as soon as the domain is verified — before a save as well
  // as after, because the values may now exist first. Before verification nothing can exist, so
  // nothing is asked.
  const values = useQuery({
    queryKey: SSO_CONNECTION_QUERY_KEY,
    queryFn: ({ signal }) => readSsoConnection(signal),
    select: decodeSsoConnection,
    // Fixed for the connection's life: an update keeps the pair, and the two writes that can mint a
    // pair — "Get Givmo's values" and a first save — seed this cache with their own answers. So
    // nothing re-reads it behind the Owner's back; a reload reads it once, and the retry on demand.
    staleTime: Infinity,
    enabled: verified,
  });

  // ★ A WRITE'S ANSWER SEEDS THE VALUES' READ (AS-WEB25), cancelling any read still in flight first
  // — or its older answer, one with no values, lands on top of the seed.
  const seedValues = async (result: unknown) => {
    await queryClient.cancelQueries({ queryKey: SSO_CONNECTION_QUERY_KEY });
    queryClient.setQueryData(SSO_CONNECTION_QUERY_KEY, result);
  };

  const start = useMutation({ mutationFn: startSsoConnection, onSuccess: seedValues });

  const save = useMutation({
    mutationFn: (body: SsoConnectionBody) => saveSsoConnection(body),
    onSuccess: async (result) => {
      await seedValues(result);
      // `can_enable_sso` is derived from BOTH preconditions, and a connection landing flips it.
      // Merge for the paint, invalidate for the derived truth.
      mergeConfig('connection', result);
      invalidate();
      toast.success(SSO_COPY.connectionSaved);
    },
  });

  const submit = () => {
    save.mutate(
      manual
        ? {
            idp_entity_id: entityId.trim(),
            idp_redirect_url: signOnUrl.trim(),
            idp_certificate_pem: certificate.trim(),
          }
        : { metadata_url: metadataUrl.trim() },
    );
  };

  const ready = manual
    ? entityId.trim().length > 0 && signOnUrl.trim().length > 0 && certificate.trim().length > 0
    : metadataUrl.trim().length > 0;

  const shown = values.data;
  const connected = configured && hasRelyingPartyValues(shown);
  const awaitingDetails = !configured && hasRelyingPartyValues(shown);

  return (
    <Card>
      <CardHeader
        className={WRAPPING_CARD_TITLE}
        title={SSO_COPY.connectionTitle}
        subtitle={SSO_COPY.connectionSubtitle}
      />
      <CardBody>
        <div className="flex flex-col gap-16" {...testIdProp(`${PREFIX}-connection`)}>
          {/* A connection needs a verified domain — the broker's `organization.domains` is its own
              login-time gate, not a label. Say so instead of offering a form that 400s. */}
          {!verified ? (
            <p
              className="font-sans text-s14 font-w400 leading-h15 tracking-normal text-text-secondary"
              {...testIdProp(`${PREFIX}-connection-needs-domain`)}
            >
              {SSO_COPY.connectionNeedsDomain}
            </p>
          ) : (
            <>
              {connected && (
                <p
                  className="font-sans text-s14 font-w600 tracking-givmo text-success-700"
                  {...testIdProp(`${PREFIX}-connection-connected`)}
                >
                  {SSO_COPY.connectionConnected}
                </p>
              )}

              <RelyingPartyValues
                values={values}
                configured={configured}
                start={start}
                saving={save.isPending}
                fallbackGuidance={config.connection.setupGuidance}
              />

              <div className="flex flex-col gap-12 pt-8">
                <div className="flex flex-col gap-4">
                  <p className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
                    {SSO_COPY.idpTitle}
                  </p>
                  {awaitingDetails && (
                    <p
                      className="font-sans text-s13 font-w400 leading-h15 tracking-normal text-text-secondary"
                      {...testIdProp(`${PREFIX}-connection-awaiting`)}
                    >
                      {SSO_COPY.idpNotYetConnected}
                    </p>
                  )}
                </div>

                {manual ? (
                  <div className="flex flex-col gap-12">
                    <Field
                      label={SSO_COPY.connectionEntityIdLabel}
                      value={entityId}
                      onChange={(event) => setEntityId(event.target.value)}
                      autoComplete="off"
                      spellCheck={false}
                      testId={`${PREFIX}-connection-entity-id`}
                    />
                    <Field
                      label={SSO_COPY.connectionSignOnUrlLabel}
                      value={signOnUrl}
                      onChange={(event) => setSignOnUrl(event.target.value)}
                      autoComplete="off"
                      spellCheck={false}
                      testId={`${PREFIX}-connection-sign-on-url`}
                    />
                    <Textarea
                      label={SSO_COPY.connectionCertificateLabel}
                      value={certificate}
                      onChange={(event) => setCertificate(event.target.value)}
                      rows={6}
                      spellCheck={false}
                      testId={`${PREFIX}-connection-certificate`}
                    />
                  </div>
                ) : (
                  <Field
                    label={SSO_COPY.connectionMetadataLabel}
                    helper={SSO_COPY.connectionMetadataHelp}
                    value={metadataUrl}
                    onChange={(event) => setMetadataUrl(event.target.value)}
                    autoComplete="off"
                    spellCheck={false}
                    testId={`${PREFIX}-connection-metadata-url`}
                  />
                )}

                {/* One sentence on why pasted XML is not offered. It is a real question an IT admin
                    asks, and answering it here is cheaper than a support conversation. */}
                <p
                  className="font-sans text-s12 font-w400 leading-h15 tracking-normal text-text-tertiary"
                  {...testIdProp(`${PREFIX}-connection-xml-note`)}
                >
                  {SSO_COPY.connectionXmlNote}
                </p>

                <div className="flex flex-wrap items-center gap-12">
                  <Button
                    appearance="saas"
                    tone="primary"
                    disabled={!ready || start.isPending}
                    loading={save.isPending}
                    onClick={submit}
                    testId={`${PREFIX}-connection-save`}
                  >
                    {SSO_COPY.connectionSave}
                  </Button>
                  <button
                    type="button"
                    onClick={() => setManual((current) => !current)}
                    className="font-sans text-s13 font-w600 tracking-givmo text-brand-600 hover:underline"
                    {...testIdProp(`${PREFIX}-connection-toggle`)}
                  >
                    {manual ? SSO_COPY.connectionMetadataToggle : SSO_COPY.connectionManualToggle}
                  </button>
                </div>

                {save.isError && (
                  <SsoErrorNote
                    error={save.error}
                    fallback={SSO_COPY.connectionSaveFailed}
                    testId={`${PREFIX}-connection-error`}
                  />
                )}
              </div>
            </>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

/**
 * Part one: Givmo's two values, which the employer's IT admin enters in their OWN identity provider
 * (the service provider's entity ID and its reply URL).
 *
 * ⚠ READ FROM THEIR OWN QUERY, NEVER FROM THE SECTION'S CONFIG (AS-WEB25). The broker assigns them per
 * connection and `GET …/sso` never carries them — it is the dark-reveal probe and asks the broker
 * nothing — so they live at `GET …/sso/connection`, which answers the PUT's and the start's shape.
 *
 * Five states, each saying only what is true:
 *   • the values — each with Copy, the two big providers' names for them, the server's NameID
 *     instruction verbatim (the values' read carries it; the section's is the fallback);
 *   • loading — skeleton rows, the first time this page asks;
 *   • failed with nothing to show — what failed, in the title's place; why, in the server's own
 *     sentence; and a retry. Only this part changes; the form and every other step stand;
 *   • nothing saved and no values yet — why they come first, and the button that gets them;
 *   • saved, and the service holds nothing for it (AS-WEB29) — the cue: what is wrong, what it
 *     breaks, and the next step. Saving again creates NEW values, which is why the cue says to
 *     replace the old ones in the identity provider.
 */
function RelyingPartyValues({
  values,
  configured,
  start,
  saving,
  fallbackGuidance,
}: {
  values: UseQueryResult<SsoConnectionState>;
  configured: boolean;
  start: UseMutationResult<unknown, Error, void>;
  /** A connection save is in flight: "Get Givmo's values" waits for it (see Step 2's header). */
  saving: boolean;
  fallbackGuidance: string | null;
}) {
  const [copyFailed, setCopyFailed] = useState(false);
  const shown = values.data;

  // Data first: a failed RE-read keeps the answer it already had, and that answer is still true.
  if (shown === undefined) {
    if (values.isError && !values.isFetching) {
      // WHAT failed leads, in the title's place — the title would promise values that are not
      // there. WHY follows in the server's own sentence (a typed `SSO_BROKER_UNAVAILABLE` says the
      // service could not be reached; the api-client gives a transport failure its own), with the
      // code a support conversation needs. The lead is not a fallback: every failure says it.
      return (
        <div
          className="flex flex-col items-start gap-8 rounded-lg border border-border-subtle bg-surface-sunken p-16"
          {...testIdProp(`${PREFIX}-relying-party-error`)}
        >
          <p className="font-sans text-s13 font-w600 tracking-givmo text-text-strong">
            {SSO_COPY.connectionValuesFailed}
          </p>
          {isGivmoError(values.error) && (
            <SsoErrorNote
              error={values.error}
              fallback={SSO_COPY.connectionValuesFailed}
              testId={`${PREFIX}-relying-party-error-note`}
            />
          )}
          <button
            type="button"
            onClick={() => void values.refetch()}
            className="font-sans text-s13 font-w600 tracking-givmo text-brand-600 hover:underline"
            {...testIdProp(`${PREFIX}-relying-party-retry`)}
          >
            {SSO_COPY.connectionValuesRetry}
          </button>
        </div>
      );
    }
    return (
      <div
        className="flex flex-col gap-8 rounded-lg border border-border-subtle bg-surface-sunken p-16"
        {...testIdProp(`${PREFIX}-relying-party-loading`)}
      >
        <Skeleton className="h-12 w-2/3" />
        <Skeleton className="h-12 w-1/2" />
      </div>
    );
  }

  if (!hasRelyingPartyValues(shown)) {
    if (configured) {
      return (
        <div
          className="flex flex-col gap-4 rounded-lg border border-warning-100 bg-warning-50 p-16"
          {...testIdProp(`${PREFIX}-relying-party-drift`)}
        >
          <p className="font-sans text-s13 font-w600 tracking-givmo text-warning-700">
            {SSO_COPY.driftTitle}
          </p>
          <p className="font-sans text-s13 font-w400 leading-h15 tracking-normal text-text-secondary">
            {SSO_COPY.driftBody}
          </p>
        </div>
      );
    }
    // A 404 on the write is the surface going dark between the section's read and the click (or a
    // backend that does not serve the route yet): an environment fact, said as the tier step says it.
    const dark = start.isError && isGivmoError(start.error) && start.error.status === 404;
    return (
      <div
        className="flex flex-col items-start gap-8 rounded-lg border border-border-subtle bg-surface-sunken p-16"
        {...testIdProp(`${PREFIX}-values-first`)}
      >
        <p className="font-sans text-s13 font-w600 tracking-givmo text-text-strong">
          {SSO_COPY.valuesTitle}
        </p>
        <p className="pb-4 font-sans text-s13 font-w400 leading-h15 tracking-normal text-text-secondary">
          {SSO_COPY.valuesFirstHelp}
        </p>
        <Button
          appearance="saas"
          tone="secondary"
          disabled={saving}
          loading={start.isPending}
          onClick={() => start.mutate()}
          testId={`${PREFIX}-values-get`}
        >
          {SSO_COPY.valuesGet}
        </Button>
        {dark ? (
          <p
            className="font-sans text-s13 font-w400 leading-h15 tracking-normal text-text-secondary"
            {...testIdProp(`${PREFIX}-values-get-unavailable`)}
          >
            {SSO_COPY.unavailable}
          </p>
        ) : (
          start.isError && (
            <SsoErrorNote
              error={start.error}
              fallback={SSO_COPY.valuesGetFailed}
              testId={`${PREFIX}-values-get-error`}
            />
          )
        )}
      </div>
    );
  }

  const guidance = shown.setupGuidance ?? fallbackGuidance;
  const copy = async (value: string) => {
    // Honest about a refusal, like the DNS hand-off block: the value stays on screen to select.
    const copied = await copyToClipboard(value);
    setCopyFailed(!copied);
    if (copied) toast.success(SSO_COPY.valueCopied);
  };

  return (
    <div
      className="flex flex-col gap-12 rounded-lg border border-border-subtle bg-surface-sunken p-16"
      {...testIdProp(`${PREFIX}-connection-configured`)}
    >
      <p className="font-sans text-s13 font-w600 tracking-givmo text-text-strong">
        {SSO_COPY.connectionConfiguredTitle}
      </p>
      {shown.relyingPartyId !== null && (
        <ValueRow
          label={SSO_COPY.relyingPartyIdLabel}
          value={shown.relyingPartyId}
          onCopy={copy}
          testId={`${PREFIX}-relying-party-id`}
        />
      )}
      {shown.relyingPartyCallback !== null && (
        <ValueRow
          label={SSO_COPY.relyingPartyCallbackLabel}
          value={shown.relyingPartyCallback}
          onCopy={copy}
          testId={`${PREFIX}-relying-party-callback`}
        />
      )}
      {copyFailed && (
        <p
          className="font-sans text-s12 text-text-tertiary"
          {...testIdProp(`${PREFIX}-value-copy-failed`)}
        >
          {SSO_COPY.valueCopyFailed}
        </p>
      )}
      <div className="flex flex-col gap-8 border-t border-border-subtle pt-12">
        <p
          className="font-sans text-s12 font-w400 leading-h15 tracking-normal text-text-tertiary"
          {...testIdProp(`${PREFIX}-values-field-names`)}
        >
          {SSO_COPY.valuesFieldNames}
        </p>
        {/* The server's NameID instruction, verbatim: it names the two failure modes the broker
            refuses before an assertion ever reaches us, which is knowledge this file does not have. */}
        {guidance !== null && (
          <p
            className="font-sans text-s12 font-w400 leading-h15 tracking-normal text-text-secondary"
            {...testIdProp(`${PREFIX}-connection-guidance`)}
          >
            {guidance}
          </p>
        )}
      </div>
    </div>
  );
}

/** One of Givmo's two values: its label and a quiet Copy on one line, the value beneath at full
 *  width (it breaks anywhere rather than pushing a phone screen sideways). */
function ValueRow({
  label,
  value,
  onCopy,
  testId,
}: {
  label: string;
  value: string;
  onCopy: (value: string) => Promise<void>;
  testId: string;
}) {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-baseline justify-between gap-12">
        <span className="font-sans text-s13 text-text-secondary">{label}</span>
        <button
          type="button"
          onClick={() => void onCopy(value)}
          className="shrink-0 font-sans text-s13 font-w600 tracking-givmo text-brand-600 hover:underline"
          {...testIdProp(`${testId}-copy`)}
        >
          {SSO_COPY.valueCopy}
        </button>
      </div>
      <span
        className="break-all font-mono text-s13 text-text-strong select-all"
        {...testIdProp(testId)}
      >
        {value}
      </span>
    </div>
  );
}

// ── Step 3 · the test connection ────────────────────────────────────────────────────────────────

function TestConnectionStep({ config }: { config: SsoConfigState }) {
  // The callback is the HOST's (`security-host.tsx`): each registers its own in the allowlist.
  const { testCallbackPath } = useSecurityHost();
  // Rehydrated from `sessionStorage` on mount, so a SAME-TAB return (pop-up blocked, or a provider
  // that forced a top-level navigation) finds the ceremony it left rather than an empty panel.
  const [testId, setTestId] = useState<string | null>(() => recallSsoTest()?.testId ?? null);
  const [popupBlocked, setPopupBlocked] = useState(false);
  const pollDeadline = useRef<number>(0);

  const start = useMutation({
    mutationFn: async () => {
      const pkce = await createPkcePair();
      const started = await startTestConnection({
        code_challenge: pkce.challenge,
        redirect_uri: ssoTestRedirectUri(testCallbackPath),
      });
      return started;
    },
    onSuccess: (result) => {
      const started = decodeTestStart(result);
      if (started.testId === null || started.authorizeUrl === null) return;
      setPopupBlocked(false);
      setTestId(started.testId);
      rememberSsoTest(started.testId);
      pollDeadline.current = Date.now() + TEST_POLL_MAX_MS;
      // ⚠ SEVER AFTER OPEN — NOT the `'noopener'` window feature, and the difference is a false
      // claim on the happy path. Passing `'noopener'` is the tidier-looking way to get the same
      // protection, but the HTML window-open steps say *"If noopener is true … then return null"*
      // (step 17) — so the handle is null on SUCCESS, indistinguishable from the pop-up having been
      // blocked, and the screen would tell every Owner to change their browser settings beside the
      // sign-in window that just opened.
      //
      // Opening normally and then clearing `opener` gets both: a truthy handle means the window
      // opened, and `window.opener = null` is defined to *"set this's browsing context's opener
      // browsing context to null"* — a browsing-context-level disown, not a JS property tweak. It
      // must happen synchronously here, while the child is still on `about:blank`: by the time the
      // broker and the employer's identity provider load in that tab the reference is already gone,
      // so they never hold a handle on this authenticated portal (reverse tabnabbing, which the
      // admin hosting sets no `Cross-Origin-Opener-Policy` to blunt).
      const opened = globalThis.open(started.authorizeUrl, '_blank');
      if (!opened) {
        setPopupBlocked(true);
        return;
      }
      try {
        (opened as Window).opener = null;
      } catch {
        // A browser that refuses the assignment leaves the handle in place. Nothing here depends on
        // it — the outcome is polled from the Owner-gated endpoint either way — so the ceremony
        // proceeds rather than being withheld over a hardening step that did not take.
      }
    },
  });

  const outcome = useQuery({
    queryKey: ['admin-company-sso-test-connection', testId],
    queryFn: ({ signal }) => readTestConnection(testId as string, signal),
    select: decodeTestConnection,
    enabled: testId !== null,
    // Poll ONLY while the server says `pending`, and only inside the deadline. An unreadable status
    // stops the poll rather than spinning forever against a terminal answer this build cannot name
    // — see `testIsPending`.
    //
    // ⚠ `refetchInterval` is handed the RAW query state, not the `select`ed value — so the decode is
    // applied here too. Reading `state.data.status` off the untransformed body would be reaching for
    // a field on `unknown`, which is exactly the untyped access this feature's decode exists to
    // prevent; it happens to be the same key, which is what makes the mistake easy to miss.
    refetchInterval: (query) =>
      testIsPending(decodeTestConnection(query.state.data).status) &&
      Date.now() < pollDeadline.current
        ? TEST_POLL_INTERVAL_MS
        : false,
  });

  const result = outcome.data;
  const presentation = testPresentation(result?.status ?? null);

  return (
    <Card>
      <CardHeader
        className={WRAPPING_CARD_TITLE}
        title={SSO_COPY.testTitle}
        subtitle={SSO_COPY.testSubtitle}
      />
      <CardBody>
        <div className="flex flex-col gap-12" {...testIdProp(`${PREFIX}-test`)}>
          {!config.connection.configured ? (
            <p
              className="font-sans text-s14 font-w400 leading-h15 tracking-normal text-text-secondary"
              {...testIdProp(`${PREFIX}-test-needs-connection`)}
            >
              {SSO_COPY.testNeedsConnection}
            </p>
          ) : (
            <>
              <p className="font-sans text-s13 font-w400 leading-h15 tracking-normal text-text-secondary">
                {SSO_COPY.testWindowNote}
              </p>

              <div className="flex flex-wrap items-center gap-12">
                <Button
                  appearance="saas"
                  tone="primary"
                  loading={start.isPending}
                  onClick={() => {
                    forgetSsoTest();
                    start.mutate();
                  }}
                  testId={`${PREFIX}-test-start`}
                >
                  {testId === null ? SSO_COPY.testStart : SSO_COPY.testRestart}
                </Button>
                {testId !== null && (
                  <button
                    type="button"
                    onClick={() => void outcome.refetch()}
                    className="font-sans text-s13 font-w600 tracking-givmo text-brand-600 hover:underline"
                    {...testIdProp(`${PREFIX}-test-check-again`)}
                  >
                    {SSO_COPY.testCheckAgain}
                  </button>
                )}
              </div>

              {popupBlocked && (
                <p
                  className="font-sans text-s13 font-w500 leading-h15 text-warning-700"
                  {...testIdProp(`${PREFIX}-test-popup-blocked`)}
                >
                  {SSO_COPY.testPopupBlocked}
                </p>
              )}

              {start.isError && (
                <SsoErrorNote
                  error={start.error}
                  fallback={SSO_COPY.testStartFailed}
                  testId={`${PREFIX}-test-start-error`}
                />
              )}
              {outcome.isError && (
                <SsoErrorNote
                  error={outcome.error}
                  fallback={SSO_COPY.testReadFailed}
                  testId={`${PREFIX}-test-read-error`}
                />
              )}

              {result !== undefined && (
                <div
                  className="flex flex-col gap-8 rounded-lg border border-border-subtle bg-surface-sunken p-16"
                  {...testIdProp(`${PREFIX}-test-outcome`)}
                >
                  <p
                    className={
                      presentation === 'passed'
                        ? 'font-sans text-s14 font-w600 tracking-givmo text-success-700'
                        : presentation === 'waiting'
                          ? 'font-sans text-s14 font-w600 tracking-givmo text-text-secondary'
                          : 'font-sans text-s14 font-w600 tracking-givmo text-warning-700'
                    }
                    {...testIdProp(`${PREFIX}-test-status`)}
                  >
                    {presentation === 'passed'
                      ? SSO_COPY.testOkTitle
                      : presentation === 'waiting'
                        ? SSO_COPY.testWaitingTitle
                        : result.status === 'expired'
                          ? SSO_COPY.testNoResultTitle
                          : SSO_COPY.testActionNeededTitle}
                  </p>

                  {/* ⚠ THE SERVER'S GUIDANCE, VERBATIM. For `expired` the heading says no result came
                      back (D-AS-84 decision 3) and the server says why it may not have: against a
                      broker that names no flow, an assertion it refuses at its own endpoint never
                      redirects back to us, so the expiry IS the signal for the Microsoft Entra
                      pairwise-identifier case and the guidance is the NameID setup instruction; a broker
                      that names its flow answers that at once (AS-BE92), so its `expired` guidance says
                      no answer came back and what to try. Rendering either as "sign-in failed" would
                      send an admin to debug the wrong system. */}
                  {result.guidance !== null && (
                    <p
                      className="font-sans text-s13 font-w400 leading-h15 tracking-normal text-text-secondary"
                      {...testIdProp(`${PREFIX}-test-guidance`)}
                    >
                      {result.guidance}
                    </p>
                  )}

                  {/* AS-BE92 — WHAT THE IDENTITY PROVIDER SAID, SET APART FROM OUR OWN WORDS. Unsigned
                      text that anyone holding the test's broker id could have written, so it never
                      takes the guidance's voice: its own block after the guidance, a label naming its
                      author in the record rows' label style, and the words as a quotation marked by a
                      leading rule (never a fill, which would invert in dark). The words keep the
                      guidance's weight, never louder: forgeable text must not outshout our advice.
                      Plain text: React escapes it, and nothing here turns a URL into a link. Capped by
                      the decode; wraps anywhere rather than push the card past a phone's edge. */}
                  {result.idpRefusal !== null && result.idpRefusal.message !== null && (
                    <div className="flex flex-col gap-4" {...testIdProp(`${PREFIX}-test-idp-said`)}>
                      <span className="font-sans text-s13 text-text-secondary">
                        {SSO_COPY.testIdpSaid}
                      </span>
                      <blockquote
                        className="border-l-2 border-border-strong pl-12 font-sans text-s13 font-w400 leading-h15 tracking-normal text-text-secondary wrap-anywhere"
                        {...testIdProp(`${PREFIX}-test-idp-message`)}
                      >
                        {result.idpRefusal.message}
                      </blockquote>
                    </div>
                  )}
                  {result.idpRefusal !== null && idpStatusLine(result.idpRefusal) !== null && (
                    <RecordRow
                      label={SSO_COPY.testIdpStatusLabel}
                      value={idpStatusLine(result.idpRefusal) as string}
                      testId={`${PREFIX}-test-idp-status`}
                    />
                  )}

                  {/* What the provider actually asserted, so the admin can see what their claim
                      mapping produced. Only ever shown to the Owner of the company that ran it. */}
                  {result.subject !== null && (
                    <RecordRow
                      label={SSO_COPY.testSubjectLabel}
                      value={result.subject}
                      testId={`${PREFIX}-test-subject`}
                    />
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

/** Stops the poll after this long. The ceremony expires server-side well inside it; this is the
 *  bound on a tab left open, not on the flow. */
const TEST_POLL_MAX_MS = 15 * 60 * 1000;

function decodeTestStart(result: unknown): { testId: string | null; authorizeUrl: string | null } {
  const body =
    typeof result === 'object' && result !== null ? (result as Record<string, unknown>) : {};
  const testId =
    typeof body.test_id === 'string' && body.test_id.trim() ? body.test_id.trim() : null;
  const authorizeUrl =
    typeof body.authorize_url === 'string' && body.authorize_url.trim()
      ? body.authorize_url.trim()
      : null;
  return { testId, authorizeUrl };
}

// ── Step 4 · the tier ───────────────────────────────────────────────────────────────────────────

function TierStep({
  config,
  companyName,
  writeConfig,
}: {
  config: SsoConfigState;
  companyName: string;
  writeConfig: (next: unknown) => void;
}) {
  const savedSignature = `${config.mode ?? 'unknown'}:${config.freshnessHours}`;
  const [draftMode, setDraftMode] = useState<SsoTier | null>(config.mode);
  const [draftFreshness, setDraftFreshness] = useState(config.freshnessHours);
  const [lockOpen, setLockOpen] = useState(false);
  const [clamped, setClamped] = useState(false);
  const [darkSurface, setDarkSurface] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [lockError, setLockError] = useState<string | null>(null);

  // Re-seeded on the stored value's SIGNATURE rather than the object's identity: any refetch of the
  // config mints a new object, and re-seeding on that would silently discard an unsaved edit.
  useEffect(() => {
    setDraftMode(config.mode);
    setDraftFreshness(config.freshnessHours);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the signature ON PURPOSE.
  }, [savedSignature]);

  const save = useMutation({
    mutationFn: async (next: { mode: SsoTier; freshnessHours: number }) =>
      saveSsoPolicy({ sso_mode: next.mode, sso_freshness_hours: next.freshnessHours }),
    onSuccess: (result, variables) => {
      // The PUT returns the WHOLE config, so it is written straight into the cache this section
      // reads — no second round trip, and no chance of the screen and the server disagreeing.
      writeConfig(result);
      const saved = decodeSsoConfig(result);
      // ⚠ A 200 IS NOT CONFIRMATION. The backend clamps a tier above `none` to `none` when a
      // precondition is missing and REPORTS it by returning the stored state. Say what was stored.
      const clampedNow = tierWasClamped(variables.mode, saved);
      setClamped(clampedNow);
      setDraftMode(saved.mode);
      setDraftFreshness(saved.freshnessHours);
      setDarkSurface(false);
      setForbidden(false);
      setLockError(null);
      setLockOpen(false);
      // ⚠ NO SUCCESS TOAST ON A CLAMP. "Your company sign-in setting is saved" is true in the narrow
      // sense and false in every sense the Owner reads it in — they asked for a tier and got `none`.
      // Pairing it with the clamp notice would have the screen congratulate and correct in the same
      // beat, and the toast is the half that gets seen. The notice is the whole message.
      if (!clampedNow) toast.success(SSO_COPY.saved);
    },
    onError: (error, variables) => {
      const message = isGivmoError(error) ? error.message : SSO_COPY.saveFailed;
      // The ceremony keeps its own failure so it is not dismissed by its own error — an Owner who
      // just typed their company name should not have to type it again to read why it refused.
      if (variables.mode === 'signin_lock') setLockError(message);
      if (isGivmoError(error) && error.status === 404) {
        setDarkSurface(true);
        return;
      }
      if (isGivmoError(error) && error.status === 403) {
        setForbidden(true);
        return;
      }
      if (variables.mode !== 'signin_lock') toast.error(message);
    },
  });

  const tiers = useMemo(() => selectableTiers(config), [config]);
  const submittable = canSubmitTier(config);
  const dirty = draftMode !== config.mode || draftFreshness !== config.freshnessHours;

  return (
    <Card>
      <CardHeader className={WRAPPING_CARD_TITLE} title={SSO_COPY.tierTitle} />
      <CardBody>
        <div className="flex flex-col gap-16" {...testIdProp(`${PREFIX}-tier`)}>
          {/* Nothing above `none` can be stored until both preconditions hold. Say which step is
              missing rather than letting the Owner discover it through a clamp. */}
          {!config.canEnableSso && (
            <p
              className="font-sans text-s13 font-w400 leading-h15 tracking-normal text-text-secondary"
              {...testIdProp(`${PREFIX}-tier-blocked`)}
            >
              {SSO_COPY.tierBlocked}
            </p>
          )}

          <RadioTileGroup
            aria-label={SSO_COPY.tierGroupLabel}
            value={draftMode ?? undefined}
            onValueChange={(next) => {
              // ★ CHOOSING L4 OPENS THE CEREMONY; IT DOES NOT STAGE A FORM VALUE. The lock is never
              // sitting in a draft that an ordinary Save could commit — the only path to
              // `signin_lock` is through the dialog's three gates.
              if (next === 'signin_lock') {
                setLockError(null);
                setLockOpen(true);
                return;
              }
              setDraftMode(next as SsoTier);
            }}
            testId={`${PREFIX}-tier-group`}
          >
            {tiers.map((tier) => {
              const copy = TIER_COPY.find((entry) => entry.mode === tier);
              return (
                <RadioTile
                  key={tier}
                  value={tier}
                  label={copy?.label ?? tier}
                  description={copy?.description ?? ''}
                  // A tier that is STORED but no longer served by this environment renders selected
                  // and unchangeable — the backend would refuse a fresh write of it, so the control
                  // says so first. Everything above `none` also waits on the two preconditions.
                  disabled={
                    !isTierWritable(config, tier) || (tier !== 'none' && !config.canEnableSso)
                  }
                  testId={`${PREFIX}-tier-${tier}`}
                />
              );
            })}
          </RadioTileGroup>

          {/* The freshness window only means anything above `none`, so it is revealed rather than
              shown-disabled: a picker that cannot affect anything invites an Owner to reason about a
              number that is not in play. The stored value is untouched while hidden. */}
          {draftMode !== null && draftMode !== 'none' && (
            <div className="flex flex-col gap-8" {...testIdProp(`${PREFIX}-freshness`)}>
              <Select
                appearance="saas"
                label={SSO_COPY.freshnessLabel}
                options={freshnessOptions(draftFreshness)}
                value={String(draftFreshness)}
                onValueChange={(next) =>
                  setDraftFreshness(parseFreshnessOption(next, draftFreshness))
                }
                testId={`${PREFIX}-freshness-select`}
              />
              <p className="font-sans text-s13 font-w400 leading-h15 tracking-normal text-text-secondary">
                {SSO_COPY.freshnessNote}
              </p>
            </div>
          )}

          {clamped && (
            <p
              className="font-sans text-s13 font-w500 leading-h15 tracking-normal text-warning-700"
              {...testIdProp(`${PREFIX}-tier-clamped`)}
            >
              {SSO_COPY.tierClamped}
            </p>
          )}

          {darkSurface && (
            <p
              className="font-sans text-s13 font-w400 leading-h15 tracking-normal text-text-secondary"
              {...testIdProp(`${PREFIX}-unavailable`)}
            >
              {SSO_COPY.unavailable}
            </p>
          )}

          {forbidden && (
            <p
              className="font-sans text-s13 font-w500 tracking-normal text-danger-700"
              {...testIdProp(`${PREFIX}-forbidden`)}
            >
              {SSO_COPY.forbidden}
            </p>
          )}

          <div>
            <Button
              appearance="saas"
              tone="primary"
              disabled={!dirty || !submittable || draftMode === null}
              loading={save.isPending && save.variables?.mode !== 'signin_lock'}
              onClick={() =>
                draftMode !== null &&
                save.mutate({ mode: draftMode, freshnessHours: draftFreshness })
              }
              testId={`${PREFIX}-save`}
            >
              {SSO_COPY.save}
            </Button>
          </div>
        </div>
      </CardBody>

      <SsoLockCeremonyDialog
        open={lockOpen}
        onOpenChange={(next) => {
          setLockOpen(next);
          if (!next) setLockError(null);
        }}
        companyName={companyName}
        submitting={save.isPending && save.variables?.mode === 'signin_lock'}
        errorMessage={lockError}
        onConfirm={() => save.mutate({ mode: 'signin_lock', freshnessHours: draftFreshness })}
      />
    </Card>
  );
}

/** Options are minted from numbers, so an unparseable value means the control was driven with
 *  something this screen never offered — keep the current window rather than inventing one. */
function parseFreshnessOption(value: string | null, fallback: number): number {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 24 && parsed <= 168 ? parsed : fallback;
}
